import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBotProtection } from '../components/BotProtection';
import '../styles/admin.scss';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { honeypotField, getSignals } = useBotProtection();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    terms: false
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.name) {
      newErrors.name = 'Nom complet est requis';
    } else if (formData.name.length < 3) {
      newErrors.name = 'Le nom doit contenir au moins 3 caractères';
    }
    
    if (!formData.email) {
      newErrors.email = 'Email est requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }
    
    if (!formData.password) {
      newErrors.password = 'Mot de passe est requis';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Le mot de passe doit contenir au moins 6 caractères';
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Confirmation du mot de passe est requise';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }
    
    if (!formData.terms) {
      newErrors.terms = 'Vous devez accepter les conditions';
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const result = await register(
        formData.name,
        formData.email,
        formData.password,
        formData.confirmPassword,
        getSignals()
      );
      if (result.success) {
        // Inscription = compte client → espace client (pas le dashboard admin)
        navigate('/my-orders');
      } else {
        const fieldErrors = {};
        if (result.errors) {
          Object.entries(result.errors).forEach(([key, messages]) => {
            const message = Array.isArray(messages) ? messages[0] : messages;
            if (key === 'password_confirmation') {
              fieldErrors.confirmPassword = message;
            } else {
              fieldErrors[key] = message;
            }
          });
        }
        setErrors({
          ...fieldErrors,
          submit: result.error || 'Erreur lors de l\'inscription'
        });
      }
    } catch (error) {
      setErrors({ submit: 'Une erreur est survenue. Veuillez réessayer.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-wrapper">
      <div className="admin-auth-page">
        <div className="auth-card">
          <div className="auth-logo">
            <Link to="/">
              <img src="/assets/images/logo/logo.png" alt="Groupe Délices" />
            </Link>
          </div>

          <h1 className="auth-title">Créer un compte</h1>
          <p className="auth-subtitle">
            Rejoignez Groupe Délices et suivez vos commandes
          </p>

          {errors.submit && (
            <div className="alert alert-danger" style={{
              padding: '0.875rem',
              background: '#fee2e2',
              color: '#dc2626',
              borderRadius: '12px',
              marginBottom: '1.5rem',
              fontSize: '0.9rem',
              textAlign: 'center'
            }}>
              {errors.submit}
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ position: 'relative' }}>
            {honeypotField}
            <div className={`form-group ${errors.name ? 'has-error' : ''}`}>
              <label htmlFor="name">Nom complet</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Nom et prénom"
                disabled={loading}
              />
              {errors.name && (
                <span className="error-message">⚠ {errors.name}</span>
              )}
            </div>

            <div className={`form-group ${errors.email ? 'has-error' : ''}`}>
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Votre email"
                disabled={loading}
              />
              {errors.email && (
                <span className="error-message">⚠ {errors.email}</span>
              )}
            </div>

            <div className={`form-group ${errors.password ? 'has-error' : ''}`}>
              <label htmlFor="password">Mot de passe</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  disabled={loading}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(prev => !prev)}
                  aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  tabIndex={-1}
                >
                  <i className={`ph ${showPassword ? 'ph-eye' : 'ph-eye-slash'}`} />
                </button>
              </div>
              {errors.password && (
                <span className="error-message">⚠ {errors.password}</span>
              )}
            </div>

            <div className={`form-group ${errors.confirmPassword ? 'has-error' : ''}`}>
              <label htmlFor="confirmPassword">Confirmer le mot de passe</label>
              <div className="password-input-wrapper">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="••••••••"
                  disabled={loading}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(prev => !prev)}
                  aria-label={showConfirmPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  tabIndex={-1}
                >
                  <i className={`ph ${showConfirmPassword ? 'ph-eye' : 'ph-eye-slash'}`} />
                </button>
              </div>
              {errors.confirmPassword && (
                <span className="error-message">⚠ {errors.confirmPassword}</span>
              )}
            </div>

            <div className={`form-group terms-group ${errors.terms ? 'has-error' : ''}`}>
              <div className="terms-check">
                <input
                  type="checkbox"
                  id="terms"
                  name="terms"
                  checked={formData.terms}
                  onChange={handleChange}
                  disabled={loading}
                />
                <label htmlFor="terms">
                  J'accepte les{' '}
                  <Link to="/terms" style={{ color: 'var(--primary-color)', fontWeight: 500 }}>
                    conditions d'utilisation
                  </Link>
                </label>
              </div>
              {errors.terms && (
                <span className="error-message">⚠ {errors.terms}</span>
              )}
            </div>

            <button 
              type="submit" 
              className="btn-primary"
              disabled={loading}
              style={{ marginTop: '1.5rem' }}
            >
              {loading ? 'Création du compte...' : 'Créer mon compte'}
            </button>
          </form>

          <div className="auth-footer">
            Vous avez déjà un compte? <Link to="/admin/login">Se connecter</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
